/**
 * IdentityCell \u2014 prefer the most actionable identifier the row carries.
 *
 * Order of preference: email \u2192 businessId \u2192 sessionId \u2192 ip.
 *
 * When the chosen identifier is NOT email/business, the cell renders a
 * small "unknown" chip alongside the value to make it obvious to
 * investigators that the customer/business is anonymous on this request
 * and they\u2019re only seeing a session or network-level fallback.
 */
import { MonoValue } from "@/components/primitives/MonoValue";
import type { EntityTypeV1 } from "@/types/v1";
import { truncateMiddle } from "@/lib/format";

interface Row {
  email?: string | null;
  businessId?: string | null;
  sessionId?: string | null;
  ip?: string | null;
}

interface Props {
  row: Row;
  /** Pivot handler (entity type + value). */
  onEntity?: (type: EntityTypeV1, value: string) => void;
  /** Truncate long values. */
  maxChars?: number;
}

export function IdentityCell({ row, onEntity, maxChars = 22 }: Props) {
  if (row.email) {
    return (
      <MonoValue
        value={truncateMiddle(row.email, maxChars)}
        onClick={onEntity ? () => onEntity("email", row.email!) : undefined}
      />
    );
  }
  if (row.businessId) {
    return (
      <MonoValue
        value={truncateMiddle(row.businessId, maxChars)}
        onClick={onEntity ? () => onEntity("business", row.businessId!) : undefined}
      />
    );
  }
  if (row.sessionId) {
    return (
      <span className="inline-flex items-center gap-1">
        <UnknownTag />
        <MonoValue
          value={truncateMiddle(row.sessionId, maxChars - 4)}
          onClick={onEntity ? () => onEntity("session", row.sessionId!) : undefined}
        />
      </span>
    );
  }
  if (row.ip) {
    return (
      <span className="inline-flex items-center gap-1">
        <UnknownTag />
        <MonoValue
          value={row.ip}
          onClick={onEntity ? () => onEntity("ip", row.ip!) : undefined}
        />
      </span>
    );
  }
  return <UnknownTag />;
}

function UnknownTag() {
  return (
    <span
      title="No email or business id was attached to this request \u2014 showing a session/IP fallback."
      className="inline-flex rounded-md border border-border bg-surface-3 px-1.5 py-0.5 text-[10px] font-medium text-fg-subtle"
    >unknown</span>
  );
}
